/**
 * Standardized React Query configuration settings
 * 
 * This module provides standardized configuration options for React Query hooks
 * in the Wealthica2 integration. Using consistent settings helps ensure predictable
 * caching behavior and optimizes performance when navigating between views.
 */

/**
 * Default React Query configuration options for Wealthica2 queries
 * 
 * CRITICAL: These settings are optimized for long-running sessions:
 * - staleTime: How long data stays "fresh" before refetching (30 minutes)
 * - gcTime: How long to keep inactive data in cache (4 hours)
 * - refetchOnMount: false ensures manual refresh only (no implicit refetch on mount)
 * - refetchOnWindowFocus: Disabled to prevent unnecessary refreshes
 * - refetchOnReconnect: Disabled for more predictable behavior
 * - retry: Set to false as failed financial queries often need user intervention
 */
export const DEFAULT_QUERY_CONFIG = {
  // Data remains fresh for 30 minutes (extended for long sessions)
  staleTime: 30 * 60 * 1000,
  
  // Unused queries are garbage collected after 4 hours (extended for long sessions)
  gcTime: 4 * 60 * 60 * 1000,
  
  // Manual refresh only: do not implicitly refetch on mount
  refetchOnMount: false,
  
  // Prevent refetching when window regains focus
  refetchOnWindowFocus: false,
  
  // Prevent refetching on network reconnection
  refetchOnReconnect: false,
  
  // Don't retry failed queries - financial data often requires user intervention
  retry: false,
};

// --- Centralized TTLs (Single Source of Truth) ---
// These TTLs govern both React Query stale times and store-level skip windows.
export const ACCOUNTS_TTL_MS = 60 * 60 * 1000; // 60 minutes
export const POSITIONS_TTL_MS = 90 * 60 * 1000; // 90 minutes

/**
 * Configuration specifically for account data
 * Extended stale time for long sessions - financial data rarely changes
 */
export const ACCOUNT_QUERY_CONFIG = {
  ...DEFAULT_QUERY_CONFIG,
  staleTime: ACCOUNTS_TTL_MS, // Account data stays fresh for 60 minutes (rarely changes)
};

/**
 * Configuration for position data
 * Extended stale time for long sessions - financial data rarely changes
 */
export const POSITION_QUERY_CONFIG = {
  ...DEFAULT_QUERY_CONFIG,
  staleTime: POSITIONS_TTL_MS, // Position data stays fresh for 90 minutes (rarely changes)
};

/**
 * Configuration for frequently changing data like prices
 * Extended stale time for long sessions while maintaining reasonable freshness
 */
export const DYNAMIC_DATA_QUERY_CONFIG = {
  ...DEFAULT_QUERY_CONFIG,
  staleTime: 5 * 60 * 1000, // Dynamic data stays fresh for 5 minutes (extended)
  gcTime: 30 * 60 * 1000, // Garbage collect after 30 minutes (extended)
};

/**
 * Helper function to create query keys with proper structure
 * This ensures consistent query key formatting across the application
 * 
 * @param baseKey - The base key for the query (e.g., 'wealthica2')
 * @param entity - The entity type (e.g., 'accounts', 'positions')
 * @param identifiers - Additional identifiers like IDs and tokens
 * @returns A properly formatted query key array
 */
export function createQueryKey(
  baseKey: string,
  entity: string,
  identifiers: Record<string, unknown>
): unknown[] {
  // Filter out undefined/null values to prevent unnecessary cache invalidation
  const filteredIdentifiers: Record<string, unknown> = {};
  
  Object.entries(identifiers).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      filteredIdentifiers[key] = value;
    }
  });
  
  return [baseKey, entity, filteredIdentifiers];
}
